import React, { useState, useCallback } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import {
  Download, FileText, Calendar, DollarSign, Loader2,
  ChevronDown, Building2, Users, Eye, FileDown
} from "lucide-react";
import { Card } from "../shared/UIComponents";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const currentYear = new Date().getFullYear();

import { fmtUSD } from "../../utils/currency";

const fmt = (v) => typeof v === "number" ? fmtUSD(v) : v;

function PreviewTable({ data, type, onClose }) {
  if (!data) return null;

  let headers = [];
  let rows = [];
  let totals = null;
  let title = "";

  if (type === "ledger" && data.distributions) {
    title = "Distribution Ledger";
    headers = ["Date", "Investment", "Principal", "Interest", "Total"];
    rows = data.distributions.map(d => [d.date, d.opportunity, fmt(d.principal_returned), fmt(d.interest_earned), fmt(d.total)]);
    totals = data.totals ? ["", "TOTALS", fmt(data.totals.total_principal), fmt(data.totals.total_interest), fmt(data.totals.total_distributions)] : null;
  } else if (type === "1099" && data.form) {
    title = `Form ${data.form} - ${data.tax_year}`;
    headers = ["Field", "Value"];
    rows = [
      ["Payer", data.payer],
      ["Recipient", data.recipient_name],
      ["Tax Year", data.tax_year],
      ["Box 1 - Interest Income", fmt(data.box_1_interest_income)],
      ["Box 3 - Interest on U.S. Savings Bonds", fmt(data.box_3_interest_on_us_savings_bonds)],
      ["Box 4 - Federal Tax Withheld", fmt(data.box_4_federal_tax_withheld)],
      ["Distribution Count", data.distribution_count],
    ];
  } else if (type === "k1" && data.k1_statements) {
    title = "Schedule K-1 Statements";
    headers = ["Fund", "Ownership", "Income", "Interest", "Cap. Gain", "Deductions", "Distributions"];
    rows = data.k1_statements.map(k => [
      k.fund_name, `${k.ownership_percentage}%`, fmt(k.ordinary_business_income),
      fmt(k.interest_income), fmt(k.long_term_capital_gain), fmt(k.deductions), fmt(k.distributions),
    ]);
    totals = data.totals ? ["TOTALS", "", fmt(data.totals.total_income), "", "", fmt(data.totals.total_deductions), fmt(data.totals.total_distributions)] : null;
  } else if (type === "bulk" && data.distributions) {
    title = "All Distributions";
    headers = ["Date", "Investor", "Investment", "Principal", "Interest", "Total"];
    rows = data.distributions.map(d => [d.date, d.investor_name, d.opportunity, fmt(d.principal_returned), fmt(d.interest_earned), fmt(d.total)]);
    totals = data.totals ? ["", "", "TOTALS", fmt(data.totals.total_principal), fmt(data.totals.total_interest), fmt(data.totals.total_distributions)] : null;
  } else if (type === "1099batch" && data.records) {
    title = `1099-INT Batch - ${data.tax_year}`;
    headers = ["Recipient", "Email", "Interest Income", "Distributions"];
    rows = data.records.map(r => [r.recipient_name, r.recipient_email, fmt(r.box_1_interest_income), r.distribution_count]);
  } else {
    return (
      <Card hover={false}>
        <div className="flex items-center justify-between mb-3">
          <span className="text-xs font-bold text-[#222] uppercase tracking-wider">Data Preview</span>
          <button onClick={onClose} className="text-xs text-[#999] hover:text-red-500 transition-colors" data-testid="close-preview">Close</button>
        </div>
        <p className="text-sm text-[#666]">No data available for the selected period.</p>
      </Card>
    );
  }

  const period = data.period ? `${data.period.start} to ${data.period.end}` : "";

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
      <Card hover={false}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <span className="text-sm font-bold text-[#222]" data-testid="preview-title">{title}</span>
            {period && <span className="text-[10px] text-[#999] ml-3">{period}</span>}
          </div>
          <button onClick={onClose} className="px-3 py-1 text-xs rounded-lg border border-[#e8e0d0] text-[#666] hover:border-red-300 hover:text-red-500 transition-colors" data-testid="close-preview">Close</button>
        </div>
        <div className="overflow-x-auto rounded-xl border border-[#e8e0d0]" data-testid="preview-table">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-[#022c22]">
                {headers.map((h, i) => (
                  <th key={i} className="px-4 py-3 text-left font-semibold text-[#e5c98b] uppercase tracking-wider whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan={headers.length} className="px-4 py-6 text-center text-[#999] italic">No records for this period</td></tr>
              ) : (
                rows.map((row, ri) => (
                  <tr key={ri} className={ri % 2 === 0 ? "bg-white" : "bg-[#faf7f2]"}>
                    {row.map((cell, ci) => (
                      <td key={ci} className="px-4 py-2.5 text-[#333] whitespace-nowrap border-b border-[#f0ebe0]">{cell}</td>
                    ))}
                  </tr>
                ))
              )}
              {totals && (
                <tr className="bg-[#FDF8EE] border-t-2 border-[#D4AF37] font-bold">
                  {totals.map((cell, ci) => (
                    <td key={ci} className="px-4 py-3 text-[#022c22] whitespace-nowrap">{cell}</td>
                  ))}
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {data.totals?.count != null && (
          <p className="text-[10px] text-[#999] mt-2 text-right">{data.totals.count} record{data.totals.count !== 1 ? "s" : ""}</p>
        )}
      </Card>
    </motion.div>
  );
}

export default function TaxExportPanel({ isAdmin = false }) {
  const [year, setYear] = useState(currentYear);
  const [useCustomRange, setUseCustomRange] = useState(false);
  const [startDate, setStartDate] = useState(`${currentYear}-01-01`);
  const [endDate, setEndDate] = useState(`${currentYear}-12-31`);
  const [downloading, setDownloading] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewType, setPreviewType] = useState(null);
  const token = localStorage.getItem("ic_token");
  const headers = { Authorization: `Bearer ${token}` };

  const dateParams = useCustomRange
    ? `start_date=${startDate}T00:00:00&end_date=${endDate}T23:59:59`
    : `year=${year}`;

  const downloadFile = useCallback(async (endpoint, filename, format = "csv") => {
    setDownloading(`${endpoint}-${format}`);
    try {
      const isPdf = format === "pdf";
      const isFile = format === "csv" || isPdf;
      const res = await axios.get(`${API}/tax-export/${endpoint}?${dateParams}&format=${format}`, {
        headers, responseType: isFile ? "blob" : "json",
      });
      if (isFile) {
        const mimeType = isPdf ? "application/pdf" : "text/csv";
        const blob = new Blob([res.data], { type: mimeType });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = filename; a.click();
        URL.revokeObjectURL(url);
      } else {
        setPreview(res.data);
      }
    } catch (err) {
      alert(err.response?.data?.detail || "Export failed");
    }
    finally { setDownloading(null); }
  }, [dateParams]);

  const handlePreview = useCallback(async (endpoint, type) => {
    setDownloading(`${endpoint}-json`);
    try {
      const res = await axios.get(`${API}/tax-export/${endpoint}?${dateParams}&format=json`, { headers });
      setPreview(res.data);
      setPreviewType(type);
    } catch (err) {
      alert(err.response?.data?.detail || "Preview failed");
    } finally {
      setDownloading(null);
    }
  }, [dateParams]);

  const investorExports = [
    { id: "ledger", label: "Distribution Ledger", desc: "All distributions with principal, interest, and totals", endpoint: "investor/ledger", icon: FileText, color: "emerald" },
    { id: "1099", label: "1099-INT Data", desc: "Interest income summary for tax filing", endpoint: "investor/1099-int", icon: DollarSign, color: "blue" },
    { id: "k1", label: "K-1 Statement", desc: "Partnership income, deductions, and distributions by fund", endpoint: "investor/k1", icon: Building2, color: "purple" },
  ];

  const adminExports = [
    { id: "bulk", label: "All Distributions", desc: "Every distribution across all investors", endpoint: "admin/bulk-distributions", icon: Users, color: "emerald" },
    { id: "1099batch", label: "1099-INT Batch", desc: "1099-INT records for all investors with interest income", endpoint: "admin/1099-batch", icon: DollarSign, color: "blue" },
  ];

  const exports = isAdmin ? [...investorExports, ...adminExports] : investorExports;

  return (
    <div className="space-y-6" data-testid="tax-export-panel">
      {/* Period Selector */}
      <Card hover={false}>
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h3 className="text-sm font-bold text-[#222] uppercase tracking-wider" data-testid="tax-export-title">Tax & Distribution Exports</h3>
            <p className="text-xs text-[#888] mt-1">Download distribution ledgers, 1099s, and K-1 statements as PDF or CSV</p>
          </div>
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 text-xs text-[#666]">
              <input type="checkbox" checked={useCustomRange} onChange={e => setUseCustomRange(e.target.checked)}
                className="rounded border-[#e8e0d0] text-emerald-600 focus:ring-emerald-500"
                data-testid="custom-range-toggle" />
              Custom range
            </label>
            {useCustomRange ? (
              <div className="flex items-center gap-2">
                <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
                  className="px-2 py-1.5 rounded-lg border border-[#e8e0d0] text-xs focus:border-emerald-400 outline-none"
                  data-testid="start-date-input" />
                <span className="text-xs text-[#999]">to</span>
                <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)}
                  className="px-2 py-1.5 rounded-lg border border-[#e8e0d0] text-xs focus:border-emerald-400 outline-none"
                  data-testid="end-date-input" />
              </div>
            ) : (
              <div className="relative">
                <select value={year} onChange={e => setYear(Number(e.target.value))}
                  className="appearance-none px-3 py-1.5 pr-8 rounded-lg border border-[#e8e0d0] text-xs font-medium bg-white focus:border-emerald-400 outline-none"
                  data-testid="year-select">
                  {[currentYear, currentYear - 1, currentYear - 2, currentYear - 3].map(y => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
                <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[#999] pointer-events-none" />
              </div>
            )}
          </div>
        </div>
      </Card>

      {/* Export Cards */}
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {exports.map((exp, i) => (
          <motion.div key={exp.id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}>
            <Card hover={false}>
              <div className="flex items-start gap-3 mb-4" data-testid={`export-card-${exp.id}`}>
                <div className={`rounded-xl p-2.5 border bg-${exp.color}-50 border-${exp.color}-200`}>
                  <exp.icon className={`h-5 w-5 text-${exp.color}-600`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-bold text-[#222]">{exp.label}</div>
                  <div className="text-[10px] text-[#888] mt-0.5">{exp.desc}</div>
                </div>
              </div>
              <div className="flex gap-2">
                <button onClick={() => downloadFile(exp.endpoint, `${exp.id}_${useCustomRange ? startDate : year}.pdf`, "pdf")}
                  disabled={!!downloading}
                  className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-[#022c22] text-[#e5c98b] text-xs font-medium hover:bg-[#064e3b] disabled:opacity-40 transition-colors"
                  data-testid={`export-pdf-${exp.id}`}>
                  {downloading === `${exp.endpoint}-pdf` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileDown className="h-3.5 w-3.5" />}
                  PDF
                </button>
                <button onClick={() => downloadFile(exp.endpoint, `${exp.id}_${useCustomRange ? startDate : year}.csv`, "csv")}
                  disabled={!!downloading}
                  className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-emerald-700 text-white text-xs font-medium hover:bg-emerald-600 disabled:opacity-40 transition-colors"
                  data-testid={`export-csv-${exp.id}`}>
                  {downloading === `${exp.endpoint}-csv` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
                  CSV
                </button>
                <button onClick={() => handlePreview(exp.endpoint, exp.id)}
                  disabled={!!downloading}
                  className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border border-[#e8e0d0] text-[#666] text-xs font-medium hover:border-emerald-300 hover:text-emerald-700 disabled:opacity-40 transition-colors"
                  data-testid={`export-preview-${exp.id}`}>
                  {downloading === `${exp.endpoint}-json` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Eye className="h-3.5 w-3.5" />}
                </button>
              </div>
            </Card>
          </motion.div>
        ))}
      </div>

      {/* Formatted Table Preview */}
      {preview && <PreviewTable data={preview} type={previewType} onClose={() => { setPreview(null); setPreviewType(null); }} />}
    </div>
  );
}
